/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * React Query hooks for transcode requests
 *
 * Related: AIO-40, AIO-41, AIO-42
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as transcodesApi from '@/lib/api/transcodes'
import { toast } from 'sonner'
import { vfxShotKeys } from './useVfxShots'

// =============================================
// QUERY KEYS
// =============================================

export const transcodeKeys = {
  all: ['transcodes'] as const,
  lists: () => [...transcodeKeys.all, 'list'] as const,
  list: (projectId: string) => [...transcodeKeys.lists(), projectId] as const,
  details: () => [...transcodeKeys.all, 'detail'] as const,
  detail: (id: string) => [...transcodeKeys.details(), id] as const,
}

// =============================================
// TRANSCODE QUERIES
// =============================================

/**
 * Fetch transcode requests for a project
 */
export function useTranscodes(projectId: string) {
  return useQuery({
    queryKey: transcodeKeys.list(projectId),
    queryFn: async () => {
      const result = await transcodesApi.listTranscodes(projectId)
      return result.transcodes
    },
    enabled: !!projectId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch a single transcode with linked VFX shots
 */
export function useTranscode(transcodeId: string) {
  return useQuery({
    queryKey: transcodeKeys.detail(transcodeId),
    queryFn: () => transcodesApi.getTranscode(transcodeId),
    enabled: !!transcodeId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  })
}

// =============================================
// TRANSCODE MUTATIONS
// =============================================

/**
 * Upload EDL file
 */
export function useUploadEDL() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: transcodesApi.uploadEDL,
    onSuccess: (data, variables) => {
      // Invalidate transcode list
      queryClient.invalidateQueries({
        queryKey: transcodeKeys.list(variables.projectId),
      })

      // Invalidate VFX shots list (since new shots may have been created/linked)
      queryClient.invalidateQueries({
        queryKey: [...vfxShotKeys.lists(), variables.projectId],
      })

      // Show success toast with details
      const { created_shots, linked_shots, errors, total_events } = data
      const successMsg = [
        created_shots.length > 0 && `Created ${created_shots.length} shot(s)`,
        linked_shots.length > 0 && `Linked ${linked_shots.length} shot(s)`,
      ]
        .filter(Boolean)
        .join(', ')

      // Show warnings if there were errors
      if (errors.length > 0) {
        toast.error(`EDL uploaded with ${errors.length} error(s)`, {
          description: `${data.transcode.transcode_id}: ${successMsg || 'No shots created'}. Errors: ${errors.slice(0, 2).join('; ')}`,
          duration: 10000, // Show for 10 seconds
        })
      } else {
        toast.success(`EDL uploaded: ${data.transcode.transcode_id}`, {
          description: successMsg || `Processed ${total_events} event(s), but no shots created/linked`,
        })
      }

    },
    onError: (error: Error) => {
      toast.error('Failed to upload EDL', {
        description: error.message,
      })
    },
  })
}

/**
 * Mark transcode as complete
 */
export function useMarkTranscodeComplete() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: transcodesApi.markTranscodeComplete,
    onSuccess: (data) => {
      // Invalidate transcode list and detail
      queryClient.invalidateQueries({
        queryKey: transcodeKeys.detail(data.id),
      })
      queryClient.invalidateQueries({
        queryKey: transcodeKeys.list(data.project_id),
      })

      toast.success(`Marked ${data.transcode_id} as complete`)
    },
    onError: (error: Error) => {
      toast.error('Failed to mark transcode complete', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete transcode
 */
export function useDeleteTranscode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ transcodeId }: { transcodeId: string; projectId: string }) =>
      transcodesApi.deleteTranscode(transcodeId),
    onSuccess: (_, variables) => {
      // Invalidate transcode list
      queryClient.invalidateQueries({
        queryKey: transcodeKeys.list(variables.projectId),
      })

      // Invalidate VFX shots (since transcode_id fields may have been cleared)
      queryClient.invalidateQueries({
        queryKey: [...vfxShotKeys.lists(), variables.projectId],
      })

      toast.success('Transcode deleted')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete transcode', {
        description: error.message,
      })
    },
  })
}
